/**
 * wa-resume — the website agent's SERVER-side continuity (FLO-544).
 *
 * FLO-329 shipped the conversation store (`WebVisitor` → `Conversation` →
 * `ChatMessage`, plus `POST /sites/web-agent/session`, which resumes a visitor's
 * newest open conversation inside a 120-minute window) and FLO-524 shipped the
 * browser half (`wa-continuity`: an open flag + a capped transcript in
 * sessionStorage, restored VISUALLY on the next page load). The two halves were
 * never connected: nothing had ever called the endpoint, so every conversation
 * row was empty and a restored rail was a screenshot of a chat the server had no
 * idea existed. This module is the connection.
 *
 * The shape of the round trip, driven from `startWebsiteAgentSession()`:
 *
 *   1. open/resume — POST the clientKey (+ our visitor token, if we have one)
 *      BEFORE the pipecat boot, fired CONCURRENTLY with the mic probe so the
 *      visitor pays max(probe, resume) rather than the sum.
 *   2. persist identity — the server mints the token; we keep it (see
 *      {@link WA_VISITOR_KEY}).
 *   3. thread it through — the returned `conversationId` rides the
 *      `/sites/start-session` body, where floe-api verifies it belongs to the
 *      site and forwards it to the spawned bot, so the turns land in THIS
 *      thread.
 *   4. reconcile — when the server resumed a conversation that actually has
 *      turns, its tail replaces the sessionStorage one ({@link
 *      reconcileRestoredMessages}).
 *
 * …and the other direction (FLO-557): {@link endConversation} tells the server
 * a thread is over, so an ended chat stops being resumable from ANY tab rather
 * than just from the one that ended it.
 *
 * Everything here is best-effort. A hung, blocked, 401'd or garbage-returning
 * resume must land the visitor exactly where FLO-524 leaves them today — a
 * fresh session with their local transcript still on screen — so the driver
 * catches everything, times out on {@link WA_RESUME_TIMEOUT_MS}, and returns
 * `null` for "no server continuity this time".
 */

import {
  WA_VISITOR_KEY,
  beginTokenAcquisition,
  clearVisitorToken,
  getStoredVisitorToken,
  pendingTokenAcquisition,
  storeVisitorToken,
} from './visitor-token';
import { capTranscript, type PersistedMessage } from './wa-continuity';

/**
 * The server-issued visitor token (`wv_…`), in localStorage — the key itself
 * (and the store + single-flight acquisition latch behind it) lives in
 * visitor-token.ts, SHARED with the visitor-events client so a first visit's
 * two token-less paths can never each mint their own WebVisitor.
 *
 * ── Why not reuse `floe_anonymous_id`? ──────────────────────────────────────
 * The SDK already keeps a stable per-browser id (anonymous-id.ts) and forwards
 * it as `userInfo.anonymousId`, so "one identity, zero new keys" would be the
 * tidier story — but the server will not accept it. `openOrResume` looks a
 * visitor up by the (siteId, token) PAIR and, when that misses, mints its own
 * token and ignores the one it was handed (conversations.service.ts: "client
 * tokens are never trusted as authoritative; only a row we issued exists").
 * That is the right call and worth keeping: this token is a BEARER credential —
 * presenting it returns the last 30 turns of that visitor's conversation — so a
 * client-CHOSEN token would turn a compromised (or merely careless) host page
 * into a cross-visitor read primitive by seeding every visitor the same id.
 * Identity still stitches server-side: `anonymousId` continues to ride
 * start-session and is what links a conversation to an EndUser.
 *
 * localStorage, not sessionStorage — deliberately the opposite of the FLO-524
 * keys. Those hold what the visitor is LOOKING at (an open panel, a transcript),
 * which must not follow them into tomorrow; this holds WHO THEY ARE, which is
 * the same long-lived thing `floe_anonymous_id` holds. Resuming stays bounded
 * regardless: the 120-minute server window, not the storage lifetime, decides
 * whether a returning visitor continues a conversation or starts a new one.
 *
 * ── Two tabs ────────────────────────────────────────────────────────────────
 * They share one identity and therefore one conversation: both resolve the same
 * visitor and both resume the visitor's NEWEST open conversation, so their turns
 * interleave into a single thread and the last writer of
 * `Conversation.pipecatSessionId` wins. Chosen over any per-tab identity because
 * the alternative — a token minted per tab — would fragment one person into N
 * visitors in the dashboard, which is a worse lie than an interleaved thread.
 * The VISUAL state stays per-tab (sessionStorage), so the tabs still look
 * independent; only the server-side thread is shared.
 */
export { WA_VISITOR_KEY };

/**
 * How long the resume round trip gets before we give up and boot a fresh
 * session. Same budget as the SDK's own `validate-client-key` call, and paid
 * concurrently with the mic probe — an unreachable API must cost the visitor a
 * resumed transcript, never their first answer.
 */
export const WA_RESUME_TIMEOUT_MS = 4000;

/**
 * Floor on the request's share of {@link WA_RESUME_TIMEOUT_MS} when a join on a
 * pending token acquisition ate most of the budget: a long join must not buy a
 * zero-ms fetch that aborts before it leaves the browser. Small on purpose —
 * the overall operation staying ~timeoutMs matters more than this last try.
 */
export const WA_RESUME_MIN_REQUEST_BUDGET_MS = 250;

/** The open/resume endpoint path, relative to `config.apiUrl`. */
export const WA_SESSION_PATH = '/sites/web-agent/session';

/**
 * The dedicated END endpoint (PR #620 r5/r6). Unlike the open endpoint — which
 * the end signal used to piggyback on as `endedConversationId` — this path
 * NEVER creates anything: a pure End no longer mints a fresh empty conversation
 * as a side effect, and the server routes the status change through its
 * finalize service (one CONVERSATION_ENDED emit, one summary).
 */
export const WA_SESSION_END_PATH = '/sites/web-agent/session/end';

/**
 * How long the END signal gets (FLO-557). Shorter than the resume budget on
 * purpose: nothing waits on this one. The ended-state UI has already painted by
 * the time it is fired, so the only thing a longer timeout buys is a request
 * outliving the visitor's interest in the page.
 */
export const WA_END_TIMEOUT_MS = 2000;

/** A successful open/resume, normalized. */
export interface ResumedConversation {
  /** The FLO-329 conversation this session belongs to. */
  conversationId: string;
  /** The server-issued visitor token (already persisted by the driver). */
  visitorToken: string;
  /** True when the server continued an existing conversation. */
  resumed: boolean;
  /**
   * K2 (PR #620 r6): true only when the resumed thread actually carries
   * content — i.e. the bot will be handed history (FLO-556 ships the tail in
   * the spawn body). `resumed` alone cannot answer that: under recording-off
   * (or any producer gap) every resume comes back with zero turns, and a
   * session that claims memory it doesn't have is the lie the honesty divider
   * exists to prevent. Derived client-side from the messages when an older
   * server doesn't send the boolean, so both server generations behave
   * identically.
   */
  historyAvailable: boolean;
  /** The conversation's trailing turns, renderable as-is. */
  messages: PersistedMessage[];
}

// ---------------------------------------------------------------------------
// Pure core
// ---------------------------------------------------------------------------

/**
 * Normalize the server's `recentMessages` into renderable turns.
 *
 * Maps the DB's roles onto the chat's (`USER` → user, `AGENT` → assistant) and
 * drops everything else: `SYSTEM`/`TOOL` rows are pipeline bookkeeping, and
 * retention-purged rows arrive with `content: null`. `createdAt` is an ISO
 * string over the wire, so it is parsed back to epoch millis.
 *
 * The output goes through {@link capTranscript} — the SAME normalizer the
 * sessionStorage path uses — so a server turn and a restored turn are
 * indistinguishable downstream: identical field set, identical caps, and the
 * positional `wa_prior_<i>` ids that keep restored bubbles from colliding with
 * this page load's live ones.
 */
export function mapServerMessages(recent: unknown): PersistedMessage[] {
  if (!Array.isArray(recent)) return [];
  const mapped = recent.map((raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const m = raw as Record<string, unknown>;
    const role = m.role === 'USER' ? 'user' : m.role === 'AGENT' ? 'assistant' : null;
    if (!role) return null;
    const parsed = typeof m.createdAt === 'string' ? Date.parse(m.createdAt) : NaN;
    return {
      role,
      content: m.content,
      timestamp: Number.isFinite(parsed) ? parsed : 0,
    };
  });
  return capTranscript(mapped);
}

/**
 * Parse an open/resume response body. Treats it as hostile — same posture as
 * `isValidationResponse` for validate-client-key: a proxy, an ad-blocker's
 * stub page or an older/newer floe-api can put anything on this wire, and the
 * only safe reading of a shape we don't recognize is "no continuity".
 */
export function parseOpenResponse(raw: unknown): ResumedConversation | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const body = raw as Record<string, unknown>;
  const conversationId = typeof body.conversationId === 'string' ? body.conversationId.trim() : '';
  const visitorToken = typeof body.visitorToken === 'string' ? body.visitorToken.trim() : '';
  // Both are load-bearing: the id is what gets threaded to the bot, the token is
  // what makes the NEXT page load the same visitor. Half an answer is no answer.
  if (!conversationId || !visitorToken) return null;
  const messages = mapServerMessages(body.recentMessages);
  return {
    conversationId,
    visitorToken,
    resumed: body.resumed === true,
    // K2: the server's word when it gives one; otherwise derived from whether
    // `recentMessages` actually carried renderable content (mapServerMessages
    // already drops SYSTEM/TOOL rows and retention-purged `content: null`), so
    // an older floe-api behaves exactly like a new one. Non-boolean junk reads
    // as absent — same hostile-wire posture as the rest of this parser.
    historyAvailable:
      typeof body.historyAvailable === 'boolean' ? body.historyAvailable : messages.length > 0,
    messages,
  };
}

/**
 * Which turns should the rail show: the server's, or the ones the tab restored?
 *
 * The server is the source of truth WHEN IT HAS ONE. Three cases keep the local
 * transcript instead, and each is a case where "the server knows better" is
 * simply false:
 *
 *   - no result (offline, timeout, 4xx, junk) — the FLO-524 fallback, verbatim.
 *   - `resumed: false` — the server opened a NEW thread (the 120-minute window
 *     lapsed while this tab stayed open). The visitor is still looking at that
 *     conversation; deleting it from under them to match a server-side window
 *     would be a visible loss with nothing gained.
 *   - resumed but EMPTY — today's reality, and the important one: no producer
 *     writes ChatMessage rows yet (pipecat doesn't consume the conversationId —
 *     see the ticket's follow-up), so every resumed conversation comes back with
 *     zero turns. Replacing a real transcript with that would delete the
 *     visitor's history on every page load. "Resumed and empty" reads as "the
 *     server has nothing to say", not "the chat was empty".
 *
 * When the server DOES have turns they replace the local list WHOLESALE, never
 * merged: both lists are positionally id'd (`wa_prior_0`, `wa_prior_1`, …), so a
 * merge would both duplicate the overlapping tail and collide React keys. The
 * server tail is a superset of what the tab persisted anyway (30 vs 25 turns,
 * and it survives a store the browser blocked or evicted).
 *
 * @returns the replacement list, or `null` for "keep what you have".
 */
export function reconcileRestoredMessages(
  result: ResumedConversation | null,
): PersistedMessage[] | null {
  if (!result || !result.resumed) return null;
  return result.messages.length > 0 ? result.messages : null;
}

/**
 * How this session's open/resume ENDED, for the continuity-honesty banner.
 *
 *   'resumed' — the server continued the visitor's thread AND it carries
 *               content; the bot gets its tail (FLO-556), so the painted
 *               transcript IS shared memory.
 *   'fresh'   — everything else: no call, a failed one (offline/4xx/junk →
 *               null), the server opening a NEW thread — or a thread resumed
 *               with NO history behind it (K2: recording-off sites persist no
 *               turns, so this is deterministic there, not an edge). The
 *               restored transcript stays on screen (deleting it would be a
 *               visible loss — see reconcileRestoredMessages), but the bot
 *               starts cold, and the view must SAY so rather than imply
 *               memory.
 */
export type ResumeOutcome = 'resumed' | 'fresh';

/** Fold an open/resume result into the outcome. A refused result (a thread
 *  this tab ended — see applyOpenedConversation) must be passed as null. */
export function resolveResumeOutcome(result: ResumedConversation | null): ResumeOutcome {
  return result?.resumed === true && result.historyAvailable ? 'resumed' : 'fresh';
}

/**
 * Should the chat render the "earlier conversation shown for reference —
 * starting fresh" divider over the restored turns? Only once the outcome is
 * KNOWN to be fresh (null = no session yet — a dormant restored rail implies
 * nothing about memory) and only when there are restored turns to disclaim.
 */
export function showsFreshStartNotice(
  outcome: ResumeOutcome | null | undefined,
  priorMessageCount: number,
): boolean {
  return outcome === 'fresh' && priorMessageCount > 0;
}

// ---------------------------------------------------------------------------
// Storage driver — delegates to the shared coordinator (visitor-token.ts):
// localStorage with a shared in-memory fallback, never throws (blocked in
// privacy modes, absent under SSR, throws on quota). The fallback is the C1
// fix: without it, a blocked store made every open re-mint a fresh WebVisitor
// while the events client kept ITS in-memory token — a deterministic split.
// ---------------------------------------------------------------------------

/** The visitor token from a previous visit (or this page load's fallback). */
export function readVisitorToken(): string | null {
  return getStoredVisitorToken();
}

/** Persist a server-issued token. Junk clears it rather than storing garbage. */
export function writeVisitorToken(token: unknown): void {
  if (typeof token !== 'string' || !token.trim()) clearVisitorToken();
  else storeVisitorToken(token);
}

/** Forget the visitor identity (teardown / hard reset). */
export { clearVisitorToken };

// ---------------------------------------------------------------------------
// Network driver
// ---------------------------------------------------------------------------

export interface OpenOrResumeOptions {
  /** `SDKConfig.apiUrl` — optional there, so optional here; absent → no call. */
  apiUrl?: string;
  clientKey?: string;
  /** The pipecat session id this page load is about to boot — the server stamps
   *  it on the conversation so a session can be traced to its thread. */
  pipecatSessionId?: string;
  /**
   * FLO-557: a thread this tab ended whose end signal may never have landed.
   * Settled on the DEDICATED end endpoint, AWAITED, before the open request
   * fires (PR #620 r6 — it used to piggyback on the open body as
   * `endedConversationId`), so this call still cannot hand that thread back —
   * with its turns — to a visitor who asked to forget it. Absent/blank → no
   * end pre-flight, and an open byte-identical to an ordinary one.
   */
  endedConversationId?: string | null;
  timeoutMs?: number;
}

/**
 * Open or resume this visitor's conversation. Resolves to `null` on ANY failure
 * — this is a best-effort enrichment of a session that must start regardless.
 *
 * Aborts on {@link WA_RESUME_TIMEOUT_MS} rather than inheriting the browser's
 * default (which can hang for minutes behind a captive portal or a stalled
 * proxy): the caller awaits this before the pipecat connect, so an unbounded
 * wait here would be an unbounded wait on the visitor's first answer.
 *
 * `endedConversationId` triggers an END pre-flight when the caller still holds
 * an unconfirmed "forget this chat" (FLO-557): the dedicated end endpoint is
 * called and AWAITED before the open request, which preserves the ordering the
 * old open-body piggyback guaranteed (end applied before the resume query runs)
 * — the only way to be sure this call cannot return the very thread the visitor
 * deleted. The pre-flight spends from THIS operation's budget, so a hung end
 * cannot stack a second full timeout onto the session boot; and if it fails
 * anyway, the SDK-side refusal (`applyOpenedConversation`) still drops a
 * handed-back ended thread wholesale, with the marker left standing for the
 * next open to retry.
 */
export async function openOrResumeConversation(
  opts: OpenOrResumeOptions,
): Promise<ResumedConversation | null> {
  const {
    apiUrl,
    clientKey,
    pipecatSessionId,
    endedConversationId,
    timeoutMs = WA_RESUME_TIMEOUT_MS,
  } = opts;
  if (!apiUrl || !clientKey) return null;

  const ended = typeof endedConversationId === 'string' ? endedConversationId.trim() : '';
  let visitorToken = readVisitorToken();

  // First visit (no token yet): coordinate with the OTHER token-less path —
  // the visitor-events bootstrap fires un-awaited at init, so it can still be
  // minting when the visitor opens the widget, and two concurrent token-less
  // requests would each make the server mint a WebVisitor (splitting one
  // person across two identities). Whichever path started first is THE
  // acquirer; this open either JOINS it (bounded by our own budget — the
  // events mint carries no abort of its own, and an unbounded wait here would
  // gate the visitor's first answer) or BECOMES it, so a concurrent events
  // flush joins us instead.
  //
  // ONE shared deadline: the join spends part of THIS operation's budget, so
  // the request below gets only the remainder (floored — see
  // {@link WA_RESUME_MIN_REQUEST_BUDGET_MS}) rather than a fresh timeoutMs,
  // which would let the whole open run ~2x the budget the caller asked for.
  let requestBudgetMs = timeoutMs;
  let settleAcquisition: (() => void) | null = null;
  if (!visitorToken) {
    const acquisition = pendingTokenAcquisition();
    if (acquisition) {
      const joinStart = Date.now();
      let joinTimer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        acquisition,
        new Promise<void>((r) => {
          joinTimer = setTimeout(r, timeoutMs);
        }),
      ]);
      clearTimeout(joinTimer);
      requestBudgetMs = Math.max(
        WA_RESUME_MIN_REQUEST_BUDGET_MS,
        timeoutMs - (Date.now() - joinStart),
      );
      visitorToken = readVisitorToken();
    }
    // A timed-out join does NOT transfer ownership. Posting /session without a
    // token while the original events mint is still in flight would mint a
    // second WebVisitor; whichever response wrote localStorage last would then
    // split this person's boot events and conversation across identities. Give
    // the caller the normal cold/null outcome instead — the agent can still
    // boot, and the next open will reuse the original mint once it settles.
    if (!visitorToken && pendingTokenAcquisition()) return null;

    // Still token-less and nothing (left) in flight → this open becomes the
    // sole acquirer, so a concurrent events flush joins it.
    if (!visitorToken) {
      settleAcquisition = beginTokenAcquisition();
    }
  }

  // FLO-557 (PR #620 r6): settle an unconfirmed End on the DEDICATED end
  // endpoint BEFORE the open fires. Awaiting it preserves the ordering the old
  // `endedConversationId` piggyback provided (the server applied it before its
  // resume query), without the piggyback's side effects (a pure End minting a
  // fresh conversation; the status write bypassing finalize). Shared budget:
  // the end spends from this operation's allowance and the open gets the
  // floored remainder. Token-gated — token-less there is no visitor to scope
  // the write to, and a token-less open mints a FRESH visitor, which cannot be
  // handed the ended thread anyway. Best-effort like everything here: on
  // failure the open proceeds, the marker stays standing, and
  // `applyOpenedConversation` refuses the thread if it comes back.
  if (ended && visitorToken) {
    const endStart = Date.now();
    await endConversation({
      apiUrl,
      clientKey,
      conversationId: ended,
      timeoutMs: Math.min(WA_END_TIMEOUT_MS, requestBudgetMs),
    });
    requestBudgetMs = Math.max(
      WA_RESUME_MIN_REQUEST_BUDGET_MS,
      requestBudgetMs - (Date.now() - endStart),
    );
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), requestBudgetMs);
  try {
    const response = await fetch(`${apiUrl}${WA_SESSION_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientKey,
        // Absent on a first-ever visit — the server mints one and hands it back.
        ...(visitorToken ? { visitorToken } : {}),
        ...(pipecatSessionId ? { pipecatSessionId } : {}),
      }),
      // Anonymous (clientKey + Origin) — no cookies on the public SDK bucket.
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const parsed = parseOpenResponse(await response.json());
    if (!parsed) return null;
    // Persist the identity as soon as it is known: the whole point of the token
    // is that the NEXT page load presents it.
    writeVisitorToken(parsed.visitorToken);
    return parsed;
  } catch {
    // Timeout, offline, CORS, a body that isn't JSON — all the same outcome.
    return null;
  } finally {
    clearTimeout(timeoutId);
    // AFTER the writeVisitorToken above: joined paths wake to re-read the
    // store. On failure they wake token-less and the first becomes the
    // replacement acquirer — same recovery as a failed events mint.
    settleAcquisition?.();
  }
}

export interface EndConversationOptions {
  apiUrl?: string;
  clientKey?: string;
  /** The thread being ended. Absent/blank → nothing to say, no call. */
  conversationId?: string | null;
  timeoutMs?: number;
}

/**
 * FLO-557: tell the server the visitor ENDED this conversation.
 *
 * The gap this closes: "End chat" was browser-local. `clearConversationContinuity()`
 * drops the tab's transcript, but `openOrResume` resolves a thread from the
 * VISITOR — newest ACTIVE/IDLE inside the 120-minute window — so the same
 * conversation was still live for every OTHER tab, and for this one after a
 * reload. The forgotten-thread marker (FLO-544) papers over that for the tab
 * that ended it; only the server can make it true everywhere.
 *
 * Posted to the DEDICATED end endpoint ({@link WA_SESSION_END_PATH}, PR #620
 * r6 — it used to piggyback on the open endpoint, which made every pure End
 * also MINT the visitor's next empty conversation as a side effect and stole
 * the ENDED transition from the server's finalize path). The dedicated route
 * never creates anything; the id is applied to the visitor resolved from the
 * token — a pointer into that visitor's own threads, not a capability over
 * anyone else's.
 *
 * ── Fire-and-forget, and strictly so ────────────────────────────────────────
 * This runs from a click whose UI has already committed. Every failure — offline,
 * a 4xx, a floe-api old enough to 404 the route entirely, a hang — resolves
 * `false` and changes nothing the visitor can see; the FLO-544 marker remains
 * the local guarantee. `keepalive` so an End immediately followed by a
 * navigation still lands, and a short abort so it cannot outlive that.
 *
 * The response is deliberately IGNORED: nothing about a chat the visitor just
 * deleted should be adopted into storage here.
 *
 * @returns whether the signal was accepted — for tests and logs, never for UI.
 */
export async function endConversation(opts: EndConversationOptions): Promise<boolean> {
  const { apiUrl, clientKey, timeoutMs = WA_END_TIMEOUT_MS } = opts;
  const conversationId = typeof opts.conversationId === 'string' ? opts.conversationId.trim() : '';
  if (!apiUrl || !clientKey || !conversationId) return false;

  // No token → the server has no visitor to scope the write to; the request
  // could only be a no-op (the dedicated route creates nothing, ever). Skip.
  const visitorToken = readVisitorToken();
  if (!visitorToken) return false;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${apiUrl}${WA_SESSION_END_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientKey, visitorToken, conversationId }),
      signal: controller.signal,
      keepalive: true,
      // Anonymous (clientKey + Origin) — no cookies on the public SDK bucket.
      credentials: 'omit',
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
